import { Children, useState } from 'react'
import { revealShown, rise, useInView } from '#app/components/ui/reveal.tsx'
import { cn } from '#app/utils/misc.tsx'

/** ms between two cards of the wave */
const stagger = 70
/** the last card's delay, so a long grid still finishes within ~1.2s */
const maxDelay = 500

/**
 * CardWave: a grid of cards that rises in a wave once it comes into view, the
 * Browse pages' use of Reveal. Every child is wrapped in an item that runs
 * Reveal's rise, one after another across the rows. One observer watches the
 * whole grid. Cards that mount after the wave, a next page or a new search,
 * are shown at once. With reduced motion every card is shown at once without
 * a transition.
 */
export function CardWave({
	as: Root = 'div',
	className,
	children,
}: {
	/** `ol` wraps every card in an `li` */
	as?: 'div' | 'ol'
	className?: string
	children: React.ReactNode
}) {
	const [ref, inView] = useInView<HTMLDivElement & HTMLOListElement>()
	const Item = Root === 'ol' ? 'li' : 'div'

	return (
		<Root ref={ref} data-motion="card-wave" className={className}>
			{Children.map(children, (child, i) => (
				<CardWaveItem as={Item} index={i} inView={inView}>
					{child}
				</CardWaveItem>
			))}
		</Root>
	)
}

function CardWaveItem({
	as: Item,
	index,
	inView,
	children,
}: {
	as: 'div' | 'li'
	index: number
	inView: boolean
	children: React.ReactNode
}) {
	// A card mounted after the wave has nothing to wait for.
	const [mountedAfterWave] = useState(inView)

	return (
		<Item
			data-slot="card-wave-item"
			className={cn(
				'transition-all motion-reduce:transition-none',
				rise.timing,
				inView ? revealShown : rise.initial,
			)}
			style={
				mountedAfterWave
					? undefined
					: { transitionDelay: `${Math.min(index * stagger, maxDelay)}ms` }
			}
		>
			{children}
		</Item>
	)
}
